import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { ArrowLeft, Shield } from 'lucide-react';

const PrivacyPolicy = ({ onClose }) => {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="sticky top-0 bg-white border-b z-10">
          <div className="flex items-center justify-between">
            <CardTitle className="text-2xl font-bold text-gray-900 flex items-center">
              <Shield className="h-6 w-6 mr-2 text-blue-600" />
              Datenschutzerklärung
            </CardTitle>
            <Button variant="ghost" onClick={onClose}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Schließen
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-6 space-y-6 text-sm leading-relaxed">
          
          {/* 1. Datenschutz auf einen Blick */}
          <section>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">1. Datenschutz auf einen Blick</h3>
            
            <h4 className="text-lg font-medium text-gray-800 mb-2">Allgemeine Hinweise</h4>
            <p className="text-gray-700 mb-4">
              Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen Daten passiert, 
              wenn Sie diese Website besuchen. Personenbezogene Daten sind alle Daten, mit denen Sie persönlich identifiziert 
              werden können. Ausführliche Informationen entnehmen Sie der nachfolgend aufgeführten Datenschutzerklärung.
            </p>
            
            <h4 className="text-lg font-medium text-gray-800 mb-2">Wer ist verantwortlich für die Datenerfassung auf dieser Website?</h4>
            <p className="text-gray-700 mb-4">
              Die Datenverarbeitung auf dieser Website erfolgt durch den Websitebetreiber Elektrotechnik Wegele. 
              Die Kontaktdaten können Sie dem Abschnitt „Hinweis zur verantwortlichen Stelle" in dieser Datenschutzerklärung entnehmen.
            </p>
            
            <h4 className="text-lg font-medium text-gray-800 mb-2">Wie erfassen wir Ihre Daten?</h4>
            <p className="text-gray-700 mb-4">
              Ihre Daten werden zum einen dadurch erhoben, dass Sie uns diese mitteilen. Das ist z. B. der Fall, wenn Sie Daten 
              in ein Kontaktformular eingeben. Andere Daten werden automatisch – oder nach Ihrer Einwilligung – beim Besuch der 
              Website durch unsere IT-Systeme erfasst. Das sind vor allem technische Daten (z. B. Internetbrowser, Betriebssystem, 
              Uhrzeit des Seitenaufrufs). Die Erfassung dieser technischen Daten erfolgt automatisch, sobald Sie diese Website betreten.
            </p>
            
            <h4 className="text-lg font-medium text-gray-800 mb-2">Wofür nutzen wir Ihre Daten?</h4>
            <p className="text-gray-700 mb-4">
              Ein Teil der Daten wird erhoben, um eine fehlerfreie Bereitstellung der Website zu gewährleisten. Andere von Ihnen 
              aktiv übermittelte Daten nutzen wir zur Bearbeitung Ihrer Anfragen (z. B. aus dem Kontaktformular). Es findet keine 
              Nutzung Ihrer Daten zu Werbezwecken oder zur Erstellung von Nutzerprofilen statt – auf den Einsatz von Analyse- oder 
              Tracking-Tools wird ausdrücklich verzichtet (siehe unten).
            </p>
            
            <h4 className="text-lg font-medium text-gray-800 mb-2">Welche Rechte haben Sie bezüglich Ihrer Daten?</h4>
            <p className="text-gray-700 mb-4">
              Sie haben jederzeit das Recht, unentgeltlich Auskunft über Herkunft, Empfänger und Zweck Ihrer gespeicherten 
              personenbezogenen Daten zu erhalten. Sie haben außerdem ein Recht, die Berichtigung unrichtiger Daten oder die 
              Löschung dieser Daten zu verlangen. Wenn Sie eine Einwilligung zur Datenverarbeitung erteilt haben, können Sie 
              diese Einwilligung jederzeit für die Zukunft widerrufen. Außerdem haben Sie das Recht, unter bestimmten Umständen 
              die Einschränkung der Verarbeitung Ihrer personenbezogenen Daten zu verlangen. Des Weiteren steht Ihnen ein 
              Beschwerderecht bei der zuständigen Aufsichtsbehörde zu. Hierzu sowie zu weiteren Fragen können Sie sich jederzeit 
              an uns wenden.
            </p>
          </section>

          {/* 2. Hosting */}
          <section>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">2. Hosting</h3>
            
            <h4 className="text-lg font-medium text-gray-800 mb-2">Externes Hosting (Emergent)</h4>
            <p className="text-gray-700 mb-4">
              Diese Website wird bei einem externen Dienstleister gehostet. Anbieter ist Emergent Labs Inc., 2380 Via Espada, 
              Pleasanton, CA 94566, USA (nachfolgend „Emergent"). Wenn Sie unsere Website besuchen, werden durch Emergent in 
              unserem Auftrag verschiedene Server-Logfiles erhoben (z.B. IP-Adresse, Zeitstempel, Browserdetails u. ä.) Weitere 
              Details können Sie der Datenschutzerklärung von Emergent entnehmen: 
              <a href="https://app.emergent.sh/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline ml-1">
                https://app.emergent.sh/privacy-policy
              </a>
            </p>
            
            <div className="bg-yellow-50 p-4 rounded-lg mb-4">
              <p className="text-yellow-900 text-sm">
                <strong>Wichtiger Hinweis zur Drittlandübertragung:</strong> Wir weisen darauf hin, dass bei Nutzung von Emergent 
                als Hosting-Anbieter eine Datenübertragung in Drittländer stattfinden kann. Insbesondere können personenbezogene 
                Daten in die USA übertragen und dort gespeichert werden. Die USA sind kein Staat mit durch die EU anerkanntem 
                Datenschutzniveau. US-Unternehmen sind ggf. verpflichtet, personenbezogene Daten an Sicherheitsbehörden herauszugeben, 
                ohne dass Sie hiergegen gerichtlich vorgehen können. In den USA kann daher möglicherweise kein Schutz Ihrer Rechte 
                in Bezug auf Ihre Daten gewährleistet werden.
              </p>
            </div>
            
            <p className="text-gray-700 mb-2">
              Die Verwendung von Emergent erfolgt auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO. Wir haben ein berechtigtes Interesse 
              an einer möglichst zuverlässigen Darstellung und Sicherheit unserer Website (technische Infrastruktur). Sofern eine 
              entsprechende Einwilligung abgefragt wurde (z. B. im Rahmen des Cookie-Banners), erfolgt die Verarbeitung ausschließlich 
              auf Grundlage von Art. 6 Abs. 1 lit. a DSGVO i. V. m. § 25 Abs. 1 TTDSG; die Einwilligung ist jederzeit widerrufbar.
            </p>
            
            <p className="text-gray-700 mb-4">
              <strong>Auftragsverarbeitung:</strong> Wir haben einen Vertrag über Auftragsverarbeitung (AVV) mit dem oben genannten 
              Anbieter geschlossen. Hierbei handelt es sich um einen datenschutzrechtlich vorgeschriebenen Vertrag, der gewährleistet, 
              dass dieser die personenbezogenen Daten unserer Websitebesucher nur nach unseren Weisungen und unter Einhaltung der 
              DSGVO verarbeitet.
            </p>
          </section>

          {/* 3. Allgemeine Hinweise und Pflichtinformationen */}
          <section>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">3. Allgemeine Hinweise und Pflichtinformationen</h3>
            
            <h4 className="text-lg font-medium text-gray-800 mb-2">Datenschutz und Verantwortlicher</h4>
            <p className="text-gray-700 mb-4">
              Wir als Betreiber dieser Seiten nehmen den Schutz Ihrer persönlichen Daten sehr ernst. Wir behandeln Ihre 
              personenbezogenen Daten vertraulich und entsprechend den gesetzlichen Datenschutzvorschriften sowie dieser 
              Datenschutzerklärung. Verantwortliche Stelle (der Datenverarbeiter) für die Datenverarbeitung auf dieser Website ist:
            </p>
            
            <div className="bg-blue-50 p-4 rounded-lg mb-4">
              <p className="text-blue-900">
                <strong>Elektrotechnik Wegele</strong> (Inhaber: Alexander Wegele)<br/>
                Wurzbergstraße 3, 86934 Reichling, Deutschland<br/>
                Telefon: +49 162 294 69 61<br/>
                E-Mail: office@elektrotechnik-wegele.de
              </p>
            </div>
            
            <p className="text-gray-700 mb-4">
              Die verantwortliche Stelle ist die natürliche oder juristische Person, die allein oder gemeinsam mit anderen über 
              die Zwecke und Mittel der Verarbeitung von personenbezogenen Daten entscheidet.
            </p>
            
            <h4 className="text-lg font-medium text-gray-800 mb-2">Speicherdauer</h4>
            <p className="text-gray-700 mb-4">
              Soweit innerhalb dieser Datenschutzerklärung keine speziellere Speicherdauer genannt wird, verbleiben Ihre 
              personenbezogenen Daten bei uns, bis der Zweck für die Datenverarbeitung entfällt. Wenn Sie z. B. eine Anfrage 
              gestellt haben und diese abschließend bearbeitet wurde, werden die dafür erhobenen Daten gelöscht. Sofern Sie 
              ein berechtigtes Löschersuchen stellen oder eine erteilte Einwilligung widerrufen, werden Ihre Daten gelöscht, 
              sofern wir keine anderweitige rechtlich zulässige Gründe für die Speicherung haben (z. B. steuer- oder 
              handelsrechtliche Aufbewahrungsfristen); im letztgenannten Fall erfolgt die Löschung nach Wegfall dieser Gründe.
            </p>
            
            <h4 className="text-lg font-medium text-gray-800 mb-2">Allgemeine Rechtsgrundlagen</h4>
            <p className="text-gray-700 mb-4">
              Die Verarbeitung personenbezogener Daten ist nur zulässig, wenn hierfür eine gültige Rechtsgrundlage nach Art. 6 
              DSGVO vorliegt. Im Einzelnen verarbeiten wir Ihre Daten nur, wenn mindestens eine der folgenden Bedingungen erfüllt ist:
            </p>
            
            <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4 ml-4">
              <li>
                <strong>Einwilligung (Art. 6 Abs. 1 lit. a DSGVO):</strong> Sie haben uns zuvor Ihre freiwillige und ausdrückliche 
                Einwilligung in die Verarbeitung der betreffenden Daten erteilt. Dies gilt auch für eventuelle Einwilligungen in 
                Datenübertragungen in Drittländer (Art. 49 Abs. 1 lit. a DSGVO) oder Einwilligungen im Sinne von § 25 Abs. 1 TTDSG 
                (z. B. für das Setzen von Cookies oder ähnlichen Technologien). Eine erteilte Einwilligung können Sie jederzeit widerrufen.
              </li>
              <li>
                <strong>Vertragserfüllung oder vorvertragliche Maßnahmen (Art. 6 Abs. 1 lit. b DSGVO):</strong> Die Verarbeitung 
                ist notwendig, um einen Vertrag zu erfüllen, dessen Vertragspartei Sie sind, oder um vorvertragliche Maßnahmen 
                durchzuführen, die auf Ihre Anfrage erfolgen.
              </li>
              <li>
                <strong>Rechtliche Verpflichtung (Art. 6 Abs. 1 lit. c DSGVO):</strong> Die Verarbeitung ist erforderlich, um 
                einer gesetzlichen Verpflichtung nachzukommen, der wir unterliegen.
              </li>
              <li>
                <strong>Berechtigtes Interesse (Art. 6 Abs. 1 lit. f DSGVO):</strong> Die Verarbeitung erfolgt zur Wahrung unserer 
                berechtigten Interessen (z. B. an der wirtschaftlichen Betriebssicherheit und Funktionalität unseres Online-Angebots) 
                oder der berechtigten Interessen eines Dritten, sofern nicht Ihre schutzwürdigen Interessen oder Grundrechte überwiegen.
              </li>
            </ul>
            
            <h4 className="text-lg font-medium text-gray-800 mb-2">SSL- bzw. TLS-Verschlüsselung</h4>
            <p className="text-gray-700 mb-4">
              Diese Seite nutzt aus Sicherheitsgründen und zum Schutz der Übertragung vertraulicher Inhalte, wie z. B. Anfragen, 
              die Sie an uns als Seitenbetreiber senden, eine SSL- bzw. TLS-Verschlüsselung. Eine verschlüsselte Verbindung erkennen 
              Sie daran, dass die Adresszeile des Browsers von „http://" auf „https://" wechselt und an dem Schloss-Symbol in Ihrer 
              Browserzeile. Wenn die SSL/TLS-Verschlüsselung aktiviert ist, können die Daten, die Sie an uns übermitteln, nicht von 
              Dritten mitgelesen werden.
            </p>
            
            <h4 className="text-lg font-medium text-gray-800 mb-2">Widerruf Ihrer Einwilligung zur Datenverarbeitung</h4>
            <p className="text-gray-700 mb-4">
              Viele Datenverarbeitungsvorgänge sind nur mit Ihrer ausdrücklichen Einwilligung möglich. Sie können eine bereits 
              erteilte Einwilligung jederzeit widerrufen. Die Rechtmäßigkeit der bis zum Widerruf erfolgten Datenverarbeitung 
              bleibt vom Widerruf unberührt.
            </p>
            
            <h4 className="text-lg font-medium text-gray-800 mb-2">Widerspruchsrecht gegen bestimmte Datenverarbeitungen (Art. 21 DSGVO)</h4>
            <div className="bg-red-50 p-4 rounded-lg mb-4">
              <p className="text-red-900 text-sm font-semibold mb-2">
                WENN DIE DATENVERARBEITUNG AUF GRUNDLAGE VON ART. 6 ABS. 1 LIT. E ODER F DSGVO ERFOLGT, HABEN SIE JEDERZEIT DAS 
                RECHT, AUS GRÜNDEN, DIE SICH AUS IHRER BESONDEREN SITUATION ERGEBEN, WIDERSPRUCH GEGEN DIE VERARBEITUNG IHRER 
                PERSONENBEZOGENEN DATEN EINZULEGEN; dies gilt auch für ein auf diese Bestimmungen gestütztes Profiling.
              </p>
              <p className="text-red-900 text-sm mb-2">
                Die jeweilige Rechtsgrundlage, auf denen eine Verarbeitung beruht, entnehmen Sie dieser Datenschutzerklärung. 
                Wenn Sie Widerspruch einlegen, werden wir Ihre betroffenen personenbezogenen Daten nicht mehr verarbeiten, es 
                sei denn, wir können zwingende schutzwürdige Gründe für die Verarbeitung nachweisen, die Ihre Interessen, Rechte 
                und Freiheiten überwiegen oder die Verarbeitung dient der Geltendmachung, Ausübung oder Verteidigung von 
                Rechtsansprüchen (Widerspruch nach Art. 21 Abs. 1 DSGVO).
              </p>
              <p className="text-red-900 text-sm">
                WERDEN IHRE PERSONENBEZOGENEN DATEN VERARBEITET, UM DIREKTWERBUNG ZU BETREIBEN, SO HABEN SIE DAS RECHT, JEDERZEIT 
                WIDERSPRUCH GEGEN DIE VERARBEITUNG SIE BETREFFENDER PERSONENBEZOGENER DATEN ZUM ZWECKE DERARTIGER WERBUNG EINZULEGEN; 
                dies gilt auch für das Profiling, soweit es mit solcher Direktwerbung in Verbindung steht. Wenn Sie widersprechen, 
                werden Ihre personenbezogenen Daten anschließend nicht mehr zum Zwecke der Direktwerbung verwendet (Widerspruch 
                nach Art. 21 Abs. 2 DSGVO).
              </p>
            </div>
            
            <h4 className="text-lg font-medium text-gray-800 mb-2">Beschwerderecht bei der zuständigen Aufsichtsbehörde</h4>
            <p className="text-gray-700 mb-4">
              Im Falle von Verstößen gegen die DSGVO steht den Betroffenen ein Beschwerderecht bei einer Aufsichtsbehörde zu 
              (Art. 77 DSGVO). Sie können sich hierfür an die Datenschutzbehörde Ihres üblichen Aufenthaltsortes, Ihres Arbeitsplatzes 
              oder des Orts des mutmaßlichen Verstoßes wenden. Das Beschwerderecht besteht nebeneinander zu anderen 
              verwaltungsrechtlichen oder gerichtlichen Rechtsbehelfen.
            </p>
            
            <h4 className="text-lg font-medium text-gray-800 mb-2">Recht auf Datenübertragbarkeit</h4>
            <p className="text-gray-700 mb-4">
              Sie haben das Recht, Daten, die wir auf Grundlage Ihrer Einwilligung oder in Erfüllung eines Vertrags automatisiert 
              verarbeiten, an sich oder an einen Dritten in einem gängigen, maschinenlesbaren Format aushändigen zu lassen. Sofern 
              Sie die direkte Übertragung der Daten an einen anderen Verantwortlichen verlangen, erfolgt dies nur, soweit es 
              technisch machbar ist.
            </p>
            
            <h4 className="text-lg font-medium text-gray-800 mb-2">Recht auf Auskunft, Berichtigung und Löschung</h4>
            <p className="text-gray-700 mb-4">
              Sie haben im Rahmen der geltenden gesetzlichen Bestimmungen jederzeit das Recht auf unentgeltliche Auskunft über 
              Ihre bei uns gespeicherten personenbezogenen Daten, deren Herkunft und Empfänger und den Zweck der Datenverarbeitung. 
              Gegebenenfalls haben Sie ein Recht auf Berichtigung falscher Daten oder Löschung dieser Daten, sofern die gesetzlichen 
              Voraussetzungen (Art. 17 DSGVO) vorliegen.
            </p>
            
            <h4 className="text-lg font-medium text-gray-800 mb-2">Recht auf Einschränkung der Verarbeitung</h4>
            <p className="text-gray-700 mb-4">
              Sie haben das Recht, die Einschränkung der Verarbeitung Ihrer personenbezogenen Daten zu verlangen. Dieses Recht 
              besteht insbesondere in folgenden Fällen: (1) wenn Sie die Richtigkeit Ihrer bei uns gespeicherten personenbezogenen 
              Daten bestreiten, für die Dauer der Überprüfung der Richtigkeit; (2) wenn die Verarbeitung unrechtmäßig ist, Sie 
              aber Löschung ablehnen und stattdessen die Einschränkung verlangen; (3) wenn wir die Daten nicht mehr benötigen, 
              Sie sie jedoch zur Ausübung oder Verteidigung von Rechtsansprüchen benötigen; oder (4) wenn Sie Widerspruch gegen 
              die Verarbeitung eingelegt haben, solange noch nicht feststeht, wessen Interessen überwiegen (Art. 18 DSGVO). 
              Wenn die Verarbeitung eingeschränkt wurde, dürfen diese Daten – abgesehen von der Speicherung – nur mit Ihrer 
              Einwilligung oder für die Begründung, Ausübung oder Verteidigung von Rechtsansprüchen oder zum Schutz der Rechte 
              einer anderen Person oder aus Gründen eines wichtigen öffentlichen Interesses verarbeitet werden.
            </p>
            
            <h4 className="text-lg font-medium text-gray-800 mb-2">Hinweis zur Datenübertragung im Internet</h4>
            <p className="text-gray-700 mb-4">
              Wir weisen Sie zudem darauf hin, dass die Datenübertragung im Internet (z. B. bei der Kommunikation per E‑Mail) 
              Sicherheitslücken aufweisen kann. Ein lückenloser Schutz der Daten vor dem Zugriff durch Dritte ist nicht möglich. 
              Dennoch treffen wir nach dem Stand der Technik angemessene Maßnahmen, um Ihre Daten zu schützen (siehe auch 
              SSL-Verschlüsselung oben).
            </p>
          </section>

          {/* 4. Kontaktformular und Kommunikation */}
          <section>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">4. Kontaktformular und Kommunikation</h3>
            
            <h4 className="text-lg font-medium text-gray-800 mb-2">Kontaktformular</h4>
            <p className="text-gray-700 mb-4">
              Wenn Sie uns per Kontaktformular Anfragen zukommen lassen, werden Ihre Angaben aus dem Formular inklusive der von 
              Ihnen angegebenen Kontaktdaten (z. B. Name, E-Mail, Telefonnummer etc.) zwecks Bearbeitung der Anfrage und für den 
              Fall von Anschlussfragen bei uns gespeichert. Diese Daten geben wir ohne Ihre Einwilligung nicht an Dritte weiter.
            </p>
            <p className="text-gray-700 mb-4">
              Die Verarbeitung der im Kontaktformular eingegebenen Daten erfolgt – je nach Inhalt Ihrer Anfrage – auf Grundlage 
              von Art. 6 Abs. 1 lit. b DSGVO (zur Erfüllung eines Vertrags oder vorvertraglicher Maßnahmen, sofern Ihre Anfrage 
              hierauf abzielt) oder auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an der effektiven 
              Bearbeitung von Anfragen, die an uns gerichtet sind). Falls wir Sie im Kontaktformular ausnahmsweise um eine 
              Einwilligung bitten sollten, und Sie diese erteilen, erfolgt die Verarbeitung auf Basis von Art. 6 Abs. 1 lit. a 
              DSGVO; eine erteilte Einwilligung können Sie jederzeit widerrufen.
            </p>
            <p className="text-gray-700 mb-4">
              Die von Ihnen im Kontaktformular eingegebenen Daten verbleiben bei uns, bis der Zweck für die Datenspeicherung 
              entfällt (z. B. nach abgeschlossener Bearbeitung Ihrer Anfrage) oder Sie uns zur Löschung auffordern oder eine 
              erteilte Einwilligung widerrufen. Zwingende gesetzliche Bestimmungen – insbesondere gesetzliche Aufbewahrungsfristen 
              – bleiben unberührt.
            </p>
            
            <h4 className="text-lg font-medium text-gray-800 mb-2">Anfrage per E-Mail oder Telefon</h4>
            <p className="text-gray-700 mb-4">
              Wenn Sie uns per E‑Mail oder Telefon kontaktieren, wird Ihre Anfrage inklusive aller daraus hervorgehenden 
              personenbezogenen Daten (E‑Mail-Adresse, Inhalt der Nachricht, Name etc.) zwecks Bearbeitung Ihres Anliegens 
              bei uns gespeichert und verarbeitet. Diese Daten geben wir nicht ohne Ihre Einwilligung weiter. Die Rechtsgrundlagen 
              entsprechen den oben zum Kontaktformular genannten (Art. 6 Abs. 1 lit. b bzw. lit. f DSGVO, je nach Art Ihres Anliegens).
            </p>
            <p className="text-gray-700 mb-4">
              Wir weisen darauf hin, dass wir für die E-Mail-Kommunikation den Dienst Microsoft Outlook nutzen. E-Mails, die Sie 
              an uns senden, werden auf den Servern unseres E-Mail-Dienstleisters (Microsoft) gespeichert. Auch hier gelten die 
              Hinweise zur Datensicherheit (siehe oben: E-Mail-Kommunikation kann Sicherheitslücken aufweisen).
            </p>
            <p className="text-gray-700 mb-4">
              Die über E-Mail oder Telefon an uns gerichteten Anfragen werden von uns vertraulich behandelt. Wir nutzen Ihre 
              Angaben ausschließlich zur Kommunikation mit Ihnen und zur Bearbeitung Ihres Anliegens. Eine darüberhinausgehende 
              Verarbeitung oder Weitergabe an Dritte erfolgt nicht – es sei denn, Sie willigen ausdrücklich darin ein oder wir 
              sind gesetzlich dazu verpflichtet.
            </p>
          </section>

          {/* 5. Analyse-Tools und Social-Media-Plug-ins */}
          <section>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">5. Analyse-Tools und Social-Media-Plug-ins</h3>
            
            <div className="bg-green-50 p-4 rounded-lg mb-4">
              <p className="text-green-900 font-semibold mb-2">
                ✅ Wir setzen auf unserer Website KEINE Analyse- oder Tracking-Tools ein
              </p>
              <p className="text-green-900 text-sm">
                Wir setzen auf unserer Website keine Analyse- oder Tracking-Tools ein, die Ihr Nutzungsverhalten auswerten würden. 
                Insbesondere verwenden wir kein Google Analytics, kein Facebook Pixel, kein Google Tag Manager und keine ähnlichen 
                Dienste zur Nutzeranalyse oder Werbung.
              </p>
            </div>
            
            <p className="text-gray-700 mb-4">
              Ebenso verwenden wir keine Social-Media-Plug-ins von Plattformen wie Facebook oder Instagram, die automatisch Daten 
              an solche Dienste übermitteln würden.
            </p>
            
            <h4 className="text-lg font-medium text-gray-800 mb-2">Social Media Links</h4>
            <p className="text-gray-700 mb-4">
              Unsere Website enthält lediglich verlinkte Icons bzw. Links zu unseren Profilen bei sozialen Netzwerken (z. B. ein 
              Link zu Instagram). Durch das Anklicken eines solchen Links verlassen Sie unsere Website und rufen die externe Seite 
              des jeweiligen Anbieters auf. Es findet keine automatische Datenübertragung an die Social-Media-Plattform statt, 
              solange Sie den Link nicht aktiv anklicken. Bitte beachten Sie, dass ab dem Zeitpunkt des Klicks die Datenverarbeitung 
              durch den jeweiligen Plattform-Betreiber erfolgt. Informationen zum Datenschutz auf der von uns verlinkten 
              Instagram-Plattform entnehmen Sie bitte direkt den Datenschutzrichtlinien von Instagram/Facebook.
            </p>
          </section>

          {/* 6. Cookies und Einwilligungs-Banner */}
          <section>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">6. Cookies und Einwilligungs-Banner</h3>
            
            <h4 className="text-lg font-medium text-gray-800 mb-2">Cookie-Banner</h4>
            <p className="text-gray-700 mb-4">
              Unsere Website verwendet ein Cookie-Einwilligungs-Banner, das beim ersten Besuch erscheint. Dieses Banner ermöglicht 
              es Ihnen, einstellig und gleichberechtigt zwischen „Alle akzeptieren", „Ablehnen" oder „Einstellungen anpassen" zu 
              wählen. Die Benutzeroberfläche entspricht den Vorgaben der DSGVO, indem alle Auswahlmöglichkeiten gleich prominent 
              dargestellt werden und Sie auch granulare Einstellungen vornehmen können.
            </p>
            
            <h4 className="text-lg font-medium text-gray-800 mb-2">Technische Speicherung Ihrer Einstellungen</h4>
            <div className="bg-blue-50 p-4 rounded-lg mb-4">
              <p className="text-blue-900 text-sm mb-2">
                <strong>Besonderer Hinweis:</strong> Trotz des vorhandenen Banners setzen wir ohne Ihre aktive Einwilligung keine 
                nicht notwendigen Cookies auf Ihrem Gerät.
              </p>
              <p className="text-blue-900 text-sm">
                Wenn Sie im Banner eine Auswahl treffen, wird Ihre Entscheidung lokal im Browser gespeichert. Hierzu verwenden 
                wir anstelle von Cookies eine lokale Speicherfunktion Ihres Browsers (Local Storage). In diesem Local Storage wird 
                lediglich vermerkt, welche Auswahl Sie getroffen haben (z. B. welche Kategorie von Einwilligungen Sie erlaubt oder 
                abgelehnt haben). Auf die im Local Storage gespeicherten Daten können Dritte nicht zugreifen; sie werden nicht an 
                Server übertragen und nicht zu Werbezwecken genutzt. Die Local-Storage-Einträge bleiben auf Ihrem Gerät gespeichert, 
                bis Sie diese selbst löschen (oder Ihren Browser-Cache leeren).
              </p>
            </div>
            
            <p className="text-gray-700 mb-4">
              Wir setzen damit ausschließlich technisch notwendige Mittel ein, um Ihre Datenschutzeinstellungen zu speichern. 
              Sofern wir rechtlich betrachtet für diese Speicherung eine Einwilligung benötigen (§ 25 TTDSG), wird diese durch 
              Ihre Auswahl im Banner implizit erteilt. Ohne zumindest die Zustimmung, Ihre Auswahl temporär zu speichern, müssten 
              wir Sie bei jedem Seitenaufruf erneut nach Ihren Cookie-Präferenzen fragen. Da unsere Website keine externen 
              Tracking-Dienste ohne Ihr Zutun nachlädt, dient das Banner vor allem Ihrer Information und der Möglichkeit, 
              potenzielle externe Inhalte (siehe unten) zu steuern.
            </p>
            
            <p className="text-gray-700 mb-4">
              <strong>Hinweis:</strong> Sie können Ihre einmal getroffenen Einstellungen zum Datenschutz über das Banner jederzeit 
              ändern. Dazu rufen Sie einfach erneut die Datenschutzeinstellungen über den entsprechenden Link/Button auf unserer 
              Website auf und treffen eine neue Auswahl.
            </p>
          </section>

          {/* 7. Google Maps */}
          <section>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">7. Google Maps (externe Kartenansicht)</h3>
            
            <p className="text-gray-700 mb-4">
              Unsere Website nutzt einen externen Kartendienst (Google Maps), um Ihnen den Standort unseres Unternehmens anzuzeigen. 
              Aus Datenschutzgründen ist Google Maps auf dieser Seite nicht direkt eingebunden, sondern nur über einen ausdrücklichen 
              Benutzerklick zugänglich („2-Klick-Lösung"). Das bedeutet: Auf unseren Seiten wird zunächst keine Karte von Google 
              Maps geladen. Erst wenn Sie den dafür vorgesehenen Button oder Link anklicken (z. B. „Karte anzeigen"), wird Google 
              Maps aktiviert.
            </p>
            
            <h4 className="text-lg font-medium text-gray-800 mb-2">Datenverarbeitung durch Google Maps</h4>
            <p className="text-gray-700 mb-4">
              Wenn Sie die Google-Maps-Funktion durch Klick aktivieren, wird eine Verbindung zu den Servern von Google hergestellt 
              und die Karte von Google Maps angezeigt. Dabei wird Ihre IP-Adresse und ggf. weitere technische Informationen (wie 
              die URL der besuchten Seite) an Google übertragen. Dies kann auch eine Speicherung von Cookies durch Google auf Ihrem 
              Gerät auslösen. Google erhält dadurch Kenntnis, dass Sie unsere Seite besucht haben und die Kartenfunktion nutzen. 
              Wenn Sie mit einem Google-Konto eingeloggt sind, können diese Daten dort Ihrem Nutzerprofil zugeordnet werden. Google 
              verwendet diese Daten möglicherweise zur Erstellung von Nutzungsprofilen und zu eigenen Zwecken wie personalisierter 
              Werbung, Marktforschung und bedarfsgerechter Gestaltung seiner Dienste. Darauf haben wir keinen Einfluss.
            </p>
            
            <h4 className="text-lg font-medium text-gray-800 mb-2">Drittlandübertragung</h4>
            <p className="text-gray-700 mb-4">
              Der Anbieter von Google Maps ist Google Ireland Ltd., Dublin, Irland. Es kann hierbei zu einer Weiterübermittlung 
              personenbezogener Daten an die Google LLC mit Sitz in den USA kommen. Für die USA liegt seit Juli 2023 ein 
              Angemessenheitsbeschluss der EU (EU-US Data Privacy Framework) vor, und Google LLC ist nach diesem Abkommen zertifiziert. 
              Dennoch weisen wir darauf hin, dass im Falle der Übertragung in die USA prinzipiell die in Punkt 2 beschriebenen 
              Risiken (siehe „Hosting/Drittstaatenübertragung") bestehen. Wir haben mit Google einen Vertrag zur Auftragsverarbeitung 
              (sofern erforderlich), und Google agiert im Rahmen der Bereitstellung von Google Maps als unser Auftragsverarbeiter 
              bzw. Dienstleister.
            </p>
            
            <h4 className="text-lg font-medium text-gray-800 mb-2">Rechtsgrundlage</h4>
            <p className="text-gray-700 mb-4">
              Google Maps wird nur nach Ihrer Einwilligung aktiviert (Art. 6 Abs. 1 lit. a DSGVO). Ihre Einwilligung erteilen Sie 
              entweder durch eine entsprechende Auswahl im Cookie-Banner (Kategorie „Externe Medien" oder ähnlich) oder durch das 
              bewusste Anklicken des „Karte anzeigen"-Buttons, was als konkludente Einwilligung gewertet wird. Diese Einwilligung 
              können Sie jederzeit mit Wirkung für die Zukunft widerrufen, indem Sie die Nutzung von Google Maps über unsere 
              Datenschutzeinstellungen deaktivieren oder in Ihrem Browser JavaScript für Google Maps blockieren.
            </p>
            
            <h4 className="text-lg font-medium text-gray-800 mb-2">Weitere Hinweise</h4>
            <p className="text-gray-700 mb-4">
              Die Nutzung von Google Maps erfolgt im Interesse einer ansprechenden Darstellung unseres Standorts und einer leichten 
              Auffindbarkeit der von uns angegebenen Orte – dies stellt im weitesten Sinne ein berechtigtes Interesse im Sinne von 
              Art. 6 Abs. 1 lit. f DSGVO dar. Allerdings wird Google Maps bei uns ausschließlich nach Ihrer Einwilligung geladen, 
              um den Anforderungen an freiwillige und informierte Zustimmung zu entsprechen. Mehr Informationen zum Umgang mit 
              Nutzerdaten finden Sie in der Datenschutzerklärung von Google.
            </p>
          </section>

          {/* 8. Online-Werbung und Google Ads */}
          <section>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">8. Online-Werbung und Google Ads</h3>
            
            <p className="text-gray-700 mb-4">
              Wir schalten Werbeanzeigen über Google Ads, um auf unsere Webseite aufmerksam zu machen (z. B. in Google-Suchergebnissen). 
              Wenn Sie über eine Google-Anzeige auf unsere Website gelangt sind, hat Google diesen Klick registriert. Auf unserer 
              Website selbst sind jedoch keine Google-Ads-Trackingcodes oder Remarketing-Tags eingebunden. Das bedeutet: Wir nutzen 
              keine Conversion-Tracking-Cookies von Google auf unserer Seite, und es werden durch Ihren Besuch unserer Website keine 
              zusätzlichen personenbezogenen Daten an Google übermittelt, die über das reine Anklicken der Anzeige hinausgehen.
            </p>
            
            <p className="text-gray-700 mb-4">
              <strong>Hinweis:</strong> Google kann in eigenen Systemen (Servern) nachvollziehen, dass Sie auf unsere Anzeige 
              geklickt haben (z. B. zu statistischen Zwecken). Diese Verarbeitung findet jedoch ausschließlich bei Google statt 
              und entzieht sich unserer Kontrolle. Wir erhalten von Google ggf. lediglich eine allgemeine Auswertung darüber, 
              welche Anzeigen zu einem Webseitenbesuch geführt haben, jedoch keine Informationen, mit denen wir Sie persönlich 
              identifizieren könnten.
            </p>
            
            <p className="text-gray-700 mb-4">
              Wir verzichten bewusst auf jegliche weitergehende Werbetechnologien wie Google Remarketing oder personalisierte 
              Werbung auf unserer Website. Es werden auch keine Profilingmaßnahmen im Zusammenhang mit Google Ads auf unserer 
              Seite durchgeführt.
            </p>
            
            <h4 className="text-lg font-medium text-gray-800 mb-2">Rechtsgrundlage</h4>
            <p className="text-gray-700 mb-4">
              Die Schaltung von Werbung auf externen Plattformen (hier: Google) erfolgt in unserem berechtigten Interesse an der 
              Vermarktung unseres Angebots (Art. 6 Abs. 1 lit. f DSGVO). Da auf unserer Website selbst keine besonderen 
              Trackingmaßnahmen von Google stattfinden, ist insoweit keine zusätzliche Einwilligung erforderlich. Sollten wir in 
              Zukunft Google Ads Conversion-Tracking einsetzen, würden wir hierfür natürlich vorab Ihre Einwilligung einholen.
            </p>
          </section>

          {/* 9. Instagram-Link und QR-Code */}
          <section>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">9. Instagram-Link und QR-Code</h3>
            
            <h4 className="text-lg font-medium text-gray-800 mb-2">Instagram-Link</h4>
            <p className="text-gray-700 mb-4">
              Wie oben unter Punkt 5 erwähnt, haben wir auf unserer Website einen Link zu unserem Instagram-Auftritt integriert 
              (erkennbar am Instagram-Logo). Dabei handelt es sich lediglich um einen Hyperlink, kein Plugin. Auf unserer Website 
              selbst fließen daher keine Daten zu Instagram, solange Sie den Link nicht anklicken. Wenn Sie den Instagram-Link 
              anklicken, verlassen Sie unsere Website und werden zu Instagram weitergeleitet. Dort gelten die Datenschutzbestimmungen 
              der Meta Platforms Ireland Ltd. (als Betreiberin von Instagram). Bitte beachten Sie, dass wir keinen Einfluss darauf 
              haben, welche Daten Instagram bei einem Besuch Ihres Profils erhebt. In der Regel verarbeitet Instagram Ihre IP-Adresse, 
              ggf. Informationen über das von Ihnen genutzte Gerät und – falls Sie in Ihrem Instagram-/Facebook-Konto eingeloggt 
              sind – ordnet den Besuch Ihrem Nutzerkonto zu. Details entnehmen Sie bitte der Datenschutzrichtlinie von Instagram.
            </p>
            
            <h4 className="text-lg font-medium text-gray-800 mb-2">QR-Code</h4>
            <p className="text-gray-700 mb-4">
              Unsere Webseite kann an einigen Stellen einen QR-Code anzeigen, z. B. um Ihnen das schnelle Abrufen bestimmter 
              Informationen mit dem Smartphone zu ermöglichen (etwa unsere Kontaktdaten oder die Anfahrt via Google Maps). Dieser 
              QR-Code ist als statisches Bild eingebunden und enthält lediglich die codierte Information bzw. URL. Solange Sie den 
              QR-Code nicht scannen, werden hierdurch keine Daten erhoben oder übertragen. Erst durch das freiwillige Scannen des 
              QR-Codes mit Ihrem Gerät öffnen Sie die enthaltene Verknüpfung. Die dann abgerufene externe Seite (z. B. Google Maps 
              oder unsere Instagram-Seite) kann wiederum Daten von Ihnen erheben – dies geschieht jedoch außerhalb unserer Webseite 
              und liegt in der Verantwortung des jeweiligen Anbieters. Wir setzen keine Analysesoftware ein, um das Scan-Verhalten 
              von QR-Codes auszuwerten, und der angezeigte QR-Code enthält keine Tracking-Mechanismen.
            </p>
          </section>

          {/* 10. Google Web Fonts */}
          <section>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">10. Google Web Fonts</h3>
            
            <h4 className="text-lg font-medium text-gray-800 mb-2">Einheitliche Schriftarten</h4>
            <p className="text-gray-700 mb-4">
              Diese Seite nutzt zur einheitlichen Darstellung von Schriftarten sogenannte Web Fonts, die von Google bereitgestellt 
              werden.
            </p>
            
            <h4 className="text-lg font-medium text-gray-800 mb-2">Lokale Einbindung</h4>
            <div className="bg-green-50 p-4 rounded-lg mb-4">
              <p className="text-green-900 text-sm">
                Die Google Fonts sind auf unserem Webserver lokal installiert, sodass beim Aufrufen unserer Seiten keine Verbindung 
                zu Servern von Google hergestellt wird. In diesem Fall findet keine Übertragung Ihrer IP-Adresse oder anderer Daten 
                an Google statt; es werden auch keine Google-Cookies für Schriftarten gesetzt.
              </p>
            </div>
            
            <h4 className="text-lg font-medium text-gray-800 mb-2">Einbindung via CDN (falls zutreffend)</h4>
            <p className="text-gray-700 mb-4">
              Sollte aus technischen Gründen doch einmal eine Einbindung der Schriftarten über das Content Delivery Network von 
              Google erfolgen (etwa wenn die lokalen Fonts nicht geladen werden können), baut Ihr Browser eine Verbindung zu einem 
              Google-Server auf. Dabei wird Ihre IP-Adresse an Google übertragen, und Google erfährt, dass unsere Website über Ihre 
              IP besucht wurde. In der Regel werden die Google-Fonts-CDN-Server in den USA betrieben. Google könnte dabei ebenfalls 
              Cookies in Ihrem Browser speichern oder bereits vorhandene Google-Cookies auslesen. Wir bemühen uns jedoch, dies zu 
              vermeiden durch die lokale Bereitstellung.
            </p>
            
            <h4 className="text-lg font-medium text-gray-800 mb-2">Rechtsgrundlage</h4>
            <p className="text-gray-700 mb-4">
              Die Nutzung von Google Web Fonts erfolgt im Interesse einer einheitlichen und ansprechenden Darstellung unserer 
              Online-Angebote. Dies stellt ein berechtigtes Interesse im Sinne von Art. 6 Abs. 1 lit. f DSGVO dar. Werden Google 
              Fonts ausnahmsweise von einem externen Google-Server geladen, erfolgt dies nur mit Ihrer Einwilligung (über unsere 
              Cookie/Consent-Einstellungen), da hier eine externe Datenübertragung stattfinden kann.
            </p>
            
            <h4 className="text-lg font-medium text-gray-800 mb-2">Weitere Informationen</h4>
            <p className="text-gray-700 mb-4">
              Allgemeine Informationen zu Google Web Fonts finden Sie hier bei Google. Die Datenschutzerklärung von Google finden 
              Sie unter folgendem Link:{' '}
              <a href="https://policies.google.com/privacy?hl=de" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                https://policies.google.com/privacy?hl=de
              </a>
            </p>
          </section>

          {/* 11. Aktualität und Änderung */}
          <section>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">11. Aktualität und Änderung dieser Datenschutzerklärung</h3>
            
            <p className="text-gray-700 mb-4">
              Wir behalten uns vor, diese Datenschutzerklärung anzupassen, damit sie stets den aktuellen rechtlichen Anforderungen 
              entspricht oder um Änderungen unserer Leistungen in der Datenschutzerklärung umzusetzen. Für Ihren erneuten Besuch 
              gilt dann die neue Datenschutzerklärung.
            </p>
          </section>

          {/* Footer */}
          <div className="text-xs text-gray-500 pt-4 border-t mt-8">
            <p><strong>Stand:</strong> 13. November 2025</p>
          </div>

        </CardContent>
      </Card>
    </div>
  );
};

export default PrivacyPolicy;
